import type { SurveyAnswers } from "@/contexts";

/**
 * Medicare Beneficiary Identifier (MBI) helpers.
 *
 * An MBI is 11 characters, printed on the Medicare card with dashes after the
 * 4th and 7th characters (e.g. 1EG4-TE5-MK73). Letters S, L, O, I, B and Z are
 * never used. See CMS's "Understanding the MBI" format specification.
 */

const ALPHA = "[AC-HJKMNP-RT-Y]";
const ALPHANUMERIC = "[0-9AC-HJKMNP-RT-Y]";

const MBI_REGEX = new RegExp(
  [
    "^[1-9]",
    ALPHA,
    ALPHANUMERIC,
    "[0-9]",
    ALPHA,
    ALPHANUMERIC,
    "[0-9]",
    ALPHA,
    ALPHA,
    "[0-9]",
    "[0-9]$",
  ].join(""),
);

/**
 * The Medicare number is collected on the eligibility check form, but it is
 * PHI and must only be persisted in `extras.medicareBeneficiaryId`. Use this
 * to drop it from survey answers before they are sent anywhere else
 * (account creation `surveyAnswers`, secure storage triage data).
 */
export const omitMedicareNumber = (
  surveyAnswers: SurveyAnswers,
): SurveyAnswers => {
  if (!surveyAnswers.eligibilityCheck?.medicareNumber) {
    return surveyAnswers;
  }
  const { medicareNumber, ...eligibilityCheck } =
    surveyAnswers.eligibilityCheck;
  return { ...surveyAnswers, eligibilityCheck };
};

const MBI_LENGTH = 11;

/**
 * Formats what the member types or pastes into the 4-3-4 display used on the
 * Medicare card (e.g. "1eg4te5mk73" -> "1EG4-TE5-MK73"). Keeps letters and
 * digits only, uppercases them and caps the value at 11 characters.
 */
export const formatMedicareNumberInput = (value: string): string => {
  const characters = value
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "")
    .slice(0, MBI_LENGTH);
  return [characters.slice(0, 4), characters.slice(4, 7), characters.slice(7)]
    .filter(Boolean)
    .join("-");
};

/** Uppercases and strips the dashes / spaces a member may type. */
export const normalizeMedicareNumber = (value: string): string =>
  value.replace(/[\s-]/g, "").toUpperCase();

/**
 * The sample MBI from CMS's "Understanding the MBI" document, which we also
 * show as the input placeholder. It matches the format but is not a real
 * number, so reject it instead of letting a member submit the placeholder.
 */
const MBI_EXAMPLE = "1EG4TE5MK73";

export const isValidMedicareNumber = (value: string): boolean => {
  const normalized = normalizeMedicareNumber(value);
  return normalized !== MBI_EXAMPLE && MBI_REGEX.test(normalized);
};
